/**
 * Palantir PMS - Software para la gestión de la industria hotelera
 * Palantir PMS - Software for hotel industry management
 * Autor / Author: Alberto Castro Ull
 * Fecha de creación / Created on: 31/07/2025
 * Todos los derechos reservados / All rights reserved.
 * Este software es propiedad de Alberto Castro Ull.
 * This software is the property of Alberto Castro Ull.
 * Su uso está restringido a clientes con licencia emitida por Markulia Group, S.L.
 * Use is restricted to clients licensed by Markulia Group, S.L.
 */

import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Modal } from "react-bootstrap";
import { readStoredUser } from "../../utils/sessionUser";

// Puedes mover este fuera si tienes selector de income_generators en el futuro
const defaultBusinessUnit = {
  income_generator_id: "",
  name: "",
  description: "",
  base_currency_code: "",
};

function BusinessUnitsList() {
  const [businessUnits, setBusinessUnits] = useState([]);
  const [loading, setLoading] = useState(true);

  // 🔵 Popup
  const [showNewPopup, setShowNewPopup] = useState(false);
  const [newUnit, setNewUnit] = useState(defaultBusinessUnit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const canRename = Number(readStoredUser()?.id) === 1;
  const [editingUnit, setEditingUnit] = useState(null);
  const [editedName, setEditedName] = useState("");
  const [renameSaving, setRenameSaving] = useState(false);
  const [renameError, setRenameError] = useState("");
  const [notice, setNotice] = useState("");
  const renameInFlight = useRef(false);

  // (Opcional) income_generators para el selector
  const [incomeGenerators, setIncomeGenerators] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [currenciesLoading, setCurrenciesLoading] = useState(true);
  const [currenciesError, setCurrenciesError] = useState("");

  // Cargar Income Generators solo si quieres un selector real, si no puedes dejarlo fuera
  useEffect(() => {
    async function fetchIG() {
      try {
        const token = localStorage.getItem("palantir_token");
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/income-generators/all`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setIncomeGenerators(res.data);
      } catch (err) {
        setIncomeGenerators([]);
      }
    }
    fetchIG();
  }, []);

  useEffect(() => {
    async function fetchCurrencies() {
      setCurrenciesLoading(true);
      setCurrenciesError("");
      try {
        const token = localStorage.getItem("palantir_token");
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/currencies`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCurrencies(
          (Array.isArray(res.data) ? res.data : [])
            .map((currency) => ({
              ...currency,
              code: String(currency?.code || "").trim().toUpperCase(),
            }))
            .filter(
              (currency) => /^[A-Z]{3}$/.test(currency.code) && currency.is_active !== false
            )
        );
      } catch (err) {
        setCurrencies([]);
        setCurrenciesError(
          err.response?.data?.error || "Could not load the active currency catalog."
        );
      } finally {
        setCurrenciesLoading(false);
      }
    }
    fetchCurrencies();
  }, []);

  useEffect(() => {
    async function fetchUnits() {
      try {
        const token = localStorage.getItem("palantir_token");
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/business-units/all`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setBusinessUnits(res.data);
      } catch (err) {
        console.error("❌ Error cargando Business Units:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchUnits();
  }, []);

  // --- FUNCION DE CREACIÓN ---
  async function handleSave() {
    const selectedCurrency = currencies.find(
      (currency) => currency.code === newUnit.base_currency_code && currency.is_active !== false
    );
    if (!selectedCurrency) {
      setError("Select an active base currency for the new Business Unit.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const token = localStorage.getItem("palantir_token");
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/business-units`,
        newUnit,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowNewPopup(false);
      setNewUnit(defaultBusinessUnit);
      // Reload tabla
      const updated = await axios.get(`${process.env.REACT_APP_API_URL}/api/business-units/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBusinessUnits(updated.data);
    } catch (err) {
      setError(err.response?.data?.error || "Error saving business unit.");
    } finally {
      setSaving(false);
    }
  }

  async function handleRename(event) {
    event.preventDefault();
    if (!canRename || !editingUnit || renameInFlight.current) return;
    const name = editedName.trim();
    if (!name || name.length > 255) {
      setRenameError("Enter a name between 1 and 255 characters.");
      return;
    }
    if (name === editingUnit.name) return;

    renameInFlight.current = true;
    setRenameSaving(true);
    setRenameError("");
    try {
      const token = localStorage.getItem("palantir_token");
      const { data } = await axios.patch(
        `${process.env.REACT_APP_API_URL}/api/business-units/${editingUnit.id}/name`,
        { name },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBusinessUnits((units) => units.map((unit) =>
        unit.id === editingUnit.id ? { ...unit, ...data } : unit
      ));
      setNotice(`Business Unit #${editingUnit.id} renamed to “${data.name}”.`);
      setEditingUnit(null);
    } catch (err) {
      setRenameError(err.response?.data?.error || "Could not rename the Business Unit. Try again.");
    } finally {
      renameInFlight.current = false;
      setRenameSaving(false);
    }
  }

  if (loading) return <div>Cargando business units...</div>;

  return (
    <div className="table-container" style={{ maxWidth: "100%", margin: "0 auto", paddingTop: "24px" }}>
      {/* --- HEADER CON BOTÓN --- */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h2 style={{ color: "#e6e6f7", fontSize: "1.4rem", fontWeight: "600", margin: 0 }}>
          Business Units
        </h2>
        <div>
          <button
            type="button"
            style={{
              border: "1px solid #30d8ff",
              color: "#30d8ff",
              backgroundColor: "transparent",
              padding: "6px 14px",
              borderRadius: "4px",
              fontSize: "0.95rem",
              fontWeight: "500",
              cursor: "pointer"
            }}
            onClick={() => {
              setShowNewPopup(true);
              setError("");
              setNewUnit(defaultBusinessUnit);
            }}
          >
            + Add Business Unit
          </button>
        </div>
      </div>

      {notice && <div className="alert alert-success py-2" role="status">{notice}</div>}

      {/* --- TABLA --- */}
      <table className="table table-dark table-hover align-middle" style={{ fontSize: "14px", minWidth: "900px" }}>
        <thead>
          <tr className="text-light" style={{ backgroundColor: "#5f2c82" }}>
            <th style={{ width: 60 }}>ID</th>
            <th style={{ width: 130 }}>Income Generator</th>
            <th style={{ width: 200 }}>Name</th>
            <th style={{ maxWidth: 280 }}>Description</th>
            <th style={{ width: 140 }}>Created At</th>
            <th style={{ width: 140 }}>Updated At</th>
            {canRename && <th style={{ width: 100 }}>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {businessUnits.length ? businessUnits.map((bu) => (
            <tr key={bu.id}>
              <td>{bu.id}</td>
              <td>{bu.income_generator_id}</td>
              <td style={{
                maxWidth: 200,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
              }} title={bu.name}>
                {bu.name}
              </td>
              <td style={{
                maxWidth: 280,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
              }} title={bu.description}>
                {bu.description}
              </td>
              <td style={{ fontSize: "13px" }}>
                {(bu.createdAt || bu.created_at)?.slice(0, 19).replace("T", " ")}
              </td>
              <td style={{ fontSize: "13px" }}>
                {(bu.updatedAt || bu.updated_at)?.slice(0, 19).replace("T", " ")}
              </td>
              {canRename && (
                <td>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-info"
                    aria-label={`Rename ${bu.name} (ID ${bu.id})`}
                    onClick={() => {
                      setEditingUnit(bu);
                      setEditedName(bu.name);
                      setRenameError("");
                      setNotice("");
                    }}
                  >
                    Rename
                  </button>
                </td>
              )}
            </tr>
          )) : (
            <tr>
              <td colSpan={canRename ? 7 : 6} className="text-center text-secondary py-4">
                No Business Units yet. Create the first one with its accounting currency.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <Modal
        show={Boolean(editingUnit)}
        onHide={() => { if (!renameInFlight.current) setEditingUnit(null); }}
        centered
        backdrop="static"
        keyboard={!renameSaving}
        contentClassName="bg-dark text-light"
        aria-labelledby="rename-business-unit-title"
      >
        <Modal.Header closeButton={!renameSaving} closeVariant="white">
          <Modal.Title id="rename-business-unit-title">Rename Business Unit</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleRename} aria-busy={renameSaving}>
          <Modal.Body>
            <p className="mb-3">{editingUnit?.name} · ID {editingUnit?.id}</p>
            <label htmlFor="rename-business-unit-name" className="form-label">New name</label>
            <input
              id="rename-business-unit-name"
              className="form-control"
              value={editedName}
              onChange={(event) => { setEditedName(event.target.value); setRenameError(""); }}
              aria-describedby="rename-business-unit-help"
              aria-invalid={Boolean(renameError)}
              maxLength={255}
              required
              autoFocus
              disabled={renameSaving}
            />
            <p id="rename-business-unit-help" className="small mt-2 mb-0">
              This name may also appear in public booking pages and reservation confirmations.
            </p>
            {renameError && <div className="text-danger mt-3" role="alert">{renameError}</div>}
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-secondary" onClick={() => setEditingUnit(null)} disabled={renameSaving}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={renameSaving || !editedName.trim() || editedName.trim() === editingUnit?.name}>
              {renameSaving ? "Saving…" : "Save name"}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* --- POPUP NUEVO --- */}
      {showNewPopup && (
        <div style={{
          position: "fixed", left: 0, top: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.5)", zIndex: 1001,
          display: "flex", alignItems: "center", justifyContent: "center"
        }}>
          <div style={{
            background: "#191933", color: "#fff", borderRadius: 10, padding: 32, minWidth: 390, boxShadow: "0 2px 14px #0009",
            border: "2px solid #333", position: "relative"
          }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4 className="mb-0">Add Business Unit</h4>
              <button onClick={() => setShowNewPopup(false)}
                style={{
                  background: "none", border: "none", color: "#fff", fontSize: 26, fontWeight: 400, cursor: "pointer"
                }}>×</button>
            </div>
            {/* --- CAMPOS DEL FORMULARIO --- */}
            <form onSubmit={e => { e.preventDefault(); handleSave(); }}>
              {/* ID, createdAt, updatedAt solo info */}
              <div className="mb-2">
                <label style={{ fontWeight: 500 }}>ID</label>
                <input value="(auto)" disabled className="form-control form-control-sm bg-dark text-light" />
              </div>
              <div className="mb-2">
                <label style={{ fontWeight: 500 }}>Income Generator</label>
                <select
                  value={newUnit.income_generator_id}
                  onChange={e => setNewUnit(u => ({ ...u, income_generator_id: e.target.value }))}
                  className="form-control form-control-sm"
                  required
                >
                  <option value="">-- Select --</option>
                  {incomeGenerators.map(ig => (
                    <option value={ig.id} key={ig.id}>{ig.name || ig.id}</option>
                  ))}
                </select>
              </div>
              <div className="mb-2">
                <label style={{ fontWeight: 500 }}>Name</label>
                <input
                  className="form-control form-control-sm"
                  value={newUnit.name}
                  onChange={e => setNewUnit(u => ({ ...u, name: e.target.value }))}
                  required
                  maxLength={255}
                />
              </div>
              <div className="mb-2">
                <label htmlFor="new-business-unit-base-currency" style={{ fontWeight: 500 }}>
                  Base currency
                </label>
                <select
                  id="new-business-unit-base-currency"
                  value={newUnit.base_currency_code}
                  onChange={e => setNewUnit(u => ({ ...u, base_currency_code: e.target.value }))}
                  className="form-control form-control-sm"
                  required
                  disabled={currenciesLoading || currencies.length === 0}
                >
                  <option value="">
                    {currenciesLoading ? "Loading currencies…" : "-- Select accounting currency --"}
                  </option>
                  {currencies.map(currency => (
                    <option value={currency.code} key={currency.code}>
                      {currency.code} · {currency.name}
                    </option>
                  ))}
                </select>
                <small className="text-secondary">
                  Required for new Business Units. Existing Business Units are not changed.
                </small>
                {currenciesError ? <div className="text-danger mt-1">{currenciesError}</div> : null}
              </div>
              <div className="mb-2">
                <label style={{ fontWeight: 500 }}>Description</label>
                <textarea
                  className="form-control form-control-sm"
                  value={newUnit.description}
                  onChange={e => setNewUnit(u => ({ ...u, description: e.target.value }))}
                  rows={2}
                  maxLength={4000}
                />
              </div>
              <div className="mb-2">
                <label style={{ fontWeight: 500 }}>Created At</label>
                <input value="(auto)" disabled className="form-control form-control-sm bg-dark text-light" />
              </div>
              <div className="mb-2">
                <label style={{ fontWeight: 500 }}>Updated At</label>
                <input value="(auto)" disabled className="form-control form-control-sm bg-dark text-light" />
              </div>
              {error && <div className="text-danger mb-2">{error}</div>}
              <div className="d-flex gap-2 mt-4 justify-content-end">
                <button type="button" className="btn btn-secondary" onClick={() => setShowNewPopup(false)} disabled={saving}>Cancel</button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={saving || currenciesLoading || currencies.length === 0}
                >
                  {saving ? "Creating..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}

export default BusinessUnitsList;
